import {
  pgSchema,
  index,
  unique,
  boolean,
  timestamp,
  integer,
  varchar,
  jsonb,
} from "drizzle-orm/pg-core"
import { id_length, ulidId } from "./shared"

export const partySchema = pgSchema("party")

export const partyRegistrationType = partySchema.table("registration_type", {
  id: integer("id").primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

  description: varchar("description", { length: 40 }).notNull(),
  typeCode: varchar("type_code", { length: 10 }).notNull(), // e.g. RMP, EX. TODO - called typeCode or typeSuffix? This suffix may be overwritten for RMP => 01

  inDefaultList: boolean("in_default_list").notNull().default(false), // e.g. true for all types that can be used for AP processes, false for Phyto Org Id

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"),
})

export const partyRegistration = partySchema.table(
  "registration",
  {
    id: ulidId(), // prefix reg.

    identifier: varchar("identifier", { length: 80 }).notNull(), // Bug #181917: widened 50 -> 80 to match Salesforce/MAPS max length
    registrationTypeId: integer("registration_type_id") // TODO - keep as integer FK or change to text? Depends on whether additional type level attributes are required
      .notNull()
      .references(() => partyRegistrationType.id, {
        onDelete: "no action",
        onUpdate: "no action",
      }),

    suffix: varchar("suffix", { length: 10 }), // RMP can have suffix => 01, 02. This is the suffix being used for the status / last updated date
    multipleSuffixes: boolean("multiple_suffixes").notNull().default(false), // RMP can have multiple suffixes under the same identifier e.g. ME103/06, ME103/07, ME103/08
    fishingVessel: boolean("fishing_vessel").notNull().default(false),
    uliParent: varchar("uli_parent_identifier", { length: 50 }), // Populated for all ULI with the MPI Risk Measure ID for the parent Risk Measure

    registrationStatus: varchar("registration_status", {
      length: 15,
      enum: [
        "ACTIVE",
        "SUSPENDED", // If Suspended flag is true in MAPS
        "SURRENDERED",
        "CANCELLED", // Includes deleted identifiers from ECME
      ],
    }).notNull(),

    effectiveDate: timestamp("effective_date", { withTimezone: true }),
    expiryDate: timestamp("expiry_date", { withTimezone: true }),

    source: varchar("source", {
      length: 15,
      enum: [
        "ECME_ACCOUNT_ID",
        "MAPS_RM",
        "MAPS_RBM",
        "LEGACY", // LEGACY is for migration of historic records
      ],
    }).notNull(),
    sourceSystemId: varchar("source_sys_id", { length: 15 }),

    // Account details
    accountName: varchar("account_name", { length: 255 }),
    nzbn: varchar("nzbn", { length: 20 }), // From Account in MAPS for Risk Measures.

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"),
  },
  (table) => {
    return {
      regIdentifier: unique("idx_registration_identifier").on(
        table.source,
        table.identifier,
      ), // Identifier must be unique within each source
    }
  },
)

export const partyRegistrationOperator = partySchema.table(
  "registration_operator",
  {
    id: ulidId().references(() => partyRegistration.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    // FUTURE - to support country listings:
    // New 'list' column is added and id + list is a composite primary key
    // Default (MAPS) operator records do not have list populated
    // Country listing records use the same registration id but have list populated
    // Template references the list which must be used for the consignor id
    // New validation rule verifies that there is an operator record for the list

    // Operator OR Location Details
    displayName: varchar("name", { length: 255 }),
    countryListingName: varchar("country_listing_name", {
      length: 255,
    }),
    businessName: varchar("business_name", { length: 255 }),
    tradingName: varchar("trading_name", { length: 255 }),
    physicalAddress: varchar("physical_address_lines", {
      length: 510,
    }), // This should be the physical address lines with line breaks
    city: varchar("physical_city", { length: 80 }), // Bug #181917: widened 40 -> 80 to match Salesforce/MAPS max length
    state: varchar("physical_state", { length: 40 }),
    countryCode: varchar("physical_country_code", { length: 2 }),

    phone: varchar("phone", { length: 40 }),
    mobile: varchar("mobile", { length: 40 }),
    email: varchar("email", { length: 80 }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"),
  },
)

export const partyRegistrationHistory = partySchema.table(
  "registration_history",
  {
    id: ulidId(), // prefix rgh
    registrationId: varchar("registration_id", { length: id_length })
      .notNull()
      .references(() => partyRegistration.id, {
        onDelete: "no action",
        onUpdate: "no action",
      }),

    identifier: varchar("identifier", { length: 50 }).notNull(), // TODO - agree length. 50 is shorter than MAPS limit, longer than cert number limit

    operatorName: varchar("operator_name", { length: 255 }), // TODO confirm MAPS lengths
    operatorAddress: varchar("physical_address_lines", {
      length: 510,
    }), // TODO - determine if this should be converted from commas to line breaks
    operatorCity: varchar("physical_city", { length: 40 }),
    operatorState: varchar("physical_state", { length: 40 }),
    operatorCountryCode: varchar("physical_country_code", {
      length: 3,
    }),

    fromDate: timestamp("from_date", { withTimezone: true }).notNull(),
    toDate: timestamp("to_date", { withTimezone: true }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"),
  },
  (table) => {
    return {
      regIdentifier: index("idx_registration_history_identifier").on(
        table.registrationId,
        table.fromDate,
        table.toDate,
      ),
    }
  },
)

export const partyUser = partySchema.table("user", {
  id: ulidId(), // prefix usr-

  systemUser: boolean("system_user").notNull(),
  g2gUser: boolean("g2g_user").notNull().default(false),

  // System User
  systemUsername: varchar("system_username", { length: 35 }),

  // All Users
  name: varchar("full_name", { length: 100 }),
  email: varchar("email", { length: 255 }),

  // Identifiers
  authenticationType: varchar("authentication_type", {
    length: 10,
    enum: [
      "AZURE_B2C", // Identified by sub_id
      "AZURE_AD", // Use this in non-production environments for local Cognito auth - identified by email
      "LEGACY", // Not linked to log in, e.g. migrated
    ],
  }),
  sub_id: varchar("sub_id", { length: 40 }), // Azure AD or B2C
  sf_user_id: varchar("sf_user_id", { length: 30 }),

  migrationAPECertId: integer("migration_ap_ecert_id"), // TODO confirm which field migration will use. int8? Person ID
  migrationMAASId: integer("migration_maas_id"), // TODO confirm which field migration will use. could use for both AP ECert and Phyto

  // Notification Preferences - from ECME
  notifyExport: boolean("notify_export").notNull().default(false),
  notifyExportStatuses: jsonb("notify_export_statuses"),
  notifyIncomingTransfer: boolean("notify_incoming_transfer")
    .notNull()
    .default(false),
  notifyIncomingTransferStatuses: jsonb("notify_incoming_transfer_statuses"),
  notifyOutgoingTransfer: boolean("notify_outgoing_transfer")
    .notNull()
    .default(false),
  notifyOutgoingTransferStatuses: jsonb("notify_outgoing_transfer_statuses"),

  status: varchar("status", {
    length: 20,
    enum: [
      // TODO - confirm enum, can/should we actually populate this in AWS for B2G users also in ECME?
      "ACTIVE",
      "SUSPENDED",
      "DELETED",
    ],
  }),

  // G2G / OCA Account Fields
  accountType: varchar("account_type", {
    length: 25,
    enum: ["G2G_SYSTEM", "OCA_BORDER_INSPECTOR", "G2G_READ_ONLY"],
  }),
  jobTitle: varchar("job_title", { length: 100 }),
  contactNumber: varchar("contact_number", { length: 40 }),
  statusReason: varchar("status_reason", { length: 50 }),
  statusComment: varchar("status_comment", { length: 1000 }),

  // Certifiers Only
  certSigningName: varchar("cert_signing_name", { length: 100 }),
  qualifications: varchar("qualifications", { length: 100 }),
  certSigningOffice: varchar("cert_signing_office", { length: 50 }),
  certSigningOrganisationId: varchar("cert_signing_organisation_id", {
    length: 15,
  }),
  certSigningOrganisationName: varchar("cert_signing_organisation_name", {
    length: 255,
  }),
  certSigningAddress: varchar("cert_signing_address", { length: 510 }),
  certSigningCountryCode: varchar("cert_signing_country_code", { length: 2 }),

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"),
})

export const partyB2GProfile = partySchema.table("b2g_profilename", {
  id: integer().primaryKey().generatedAlwaysAsIdentity({ startWith: 1 }),

  userId: varchar("user_id", { length: 30 })
    .notNull()
    .references(() => partyUser.id, { onDelete: "cascade" }),
  profileName: varchar("profile_name", { length: 20 }).notNull(),

  active: boolean("active").notNull().default(true), // TODO Do we actually need to be able to remove old profile names? Should be taken in conjunction with user status

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"),
})

export const partyUserRoleConstraint = partySchema.table(
  "user_role_constraint",
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity({ startWith: 1 }),

    userId: varchar("user_id", { length: 30 })
      .notNull()
      .references(() => partyUser.id, { onDelete: "cascade" }),

    role: varchar("role", { length: 50 }).notNull(),

    constraintType: varchar("constraint_type", {
      length: 20,
      enum: ["REGISTRATION", "AGENCY", "COUNTRY", "PORT", "CERT_TYPE"],
    }).notNull(),
    constraintValue: varchar("value", { length: 50 }).notNull(),

    registrationId: varchar("registration_id", { length: 30 }).references(
      () => partyRegistration.id,
      { onDelete: "cascade" },
    ),
    agencyId: varchar("agency_id", { length: 30 }).references(
      () => partyAgency.id,
      { onDelete: "cascade" },
    ),

    active: boolean("active").notNull().default(true),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"),
  },
  (table) => {
    return {
      userConstraint: unique("idx_user_constraint").on(
        table.userId,
        table.role,
        table.constraintType,
        table.constraintValue,
      ),
    }
  },
)

export const partyAgency = partySchema.table("agency", {
  id: ulidId(), // Prefix agy-
  identifier: varchar("identifier", { length: 30 }).notNull(),
  agencyType: varchar("agency_type", { length: 15 }).notNull(),

  displayName: varchar("name", { length: 255 }),
  physicalAddress: varchar("physical_address_lines", {
    length: 510,
  }), // Address with line breaks
  city: varchar("physical_city", { length: 40 }),
  state: varchar("physical_state", { length: 40 }),
  countryCode: varchar("physical_country_code", { length: 2 }),

  sourceSystemId: varchar("source_sys_id", { length: 15 }),

  active: boolean("active").notNull().default(true),

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"),
})

export const partyAgencyHistory = partySchema.table("agency_history", {
  id: ulidId(), // prefix agh
  agencyId: varchar("agency_id", { length: id_length })
    .notNull()
    .references(() => partyAgency.id, {
      onDelete: "no action",
      onUpdate: "no action",
    }),

  identifier: varchar("identifier", { length: 30 }).notNull(),
  agencyType: varchar("agency_type", { length: 15 }).notNull(),

  displayName: varchar("name", { length: 255 }),
  physicalAddress: varchar("physical_address_lines", {
    length: 510,
  }), // Address with line breaks
  city: varchar("physical_city", { length: 40 }),
  state: varchar("physical_state", { length: 40 }),
  countryCode: varchar("physical_country_code", { length: 2 }),

  fromDate: timestamp("from_date", { withTimezone: true }).notNull(),
  toDate: timestamp("to_date", { withTimezone: true }),

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"),
})
